import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrown } from '@fortawesome/free-solid-svg-icons';
import { useSelector, useDispatch } from 'react-redux';
import { ThemeProvider, DefaultTheme } from 'styled-components';
import { AppDispatch, RootState } from '../../redux/store';
import {
  fetchLoyaltyConfig,
  fetchCustomerLoyalties,
  setMerchantId,
  setCurrentPage,
  setLimit,
} from '../../redux/Loyalty';
import SummaryStats from './SummaryStats';
import Configuration from './Configuration';
import CustomerTable from './CustomerTable';
import CustomersDropdown from './CustomersDropdown';
import {
  Container,
  HeaderContainer,
  HeaderContent,
  IconWrapper,
  HeaderTitle,
  HeaderSubtitle,
  CustomersCard,
  CustomersHeader,
  CustomersHeaderContent,
  HeaderText,
  CustomerCountBadge,
} from './styles';

const Loyalty: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { config, customerLoyalties, totalPages, totalCount, currentPage, limit, merchantId, loading } =
    useSelector((state: RootState) => state.loyalty);
  const { isDarkMode } = useSelector((state: RootState) => state.theme);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';

  useEffect(() => {
    dispatch(fetchLoyaltyConfig());
  }, [dispatch]);

  useEffect(() => {
    if (isAdmin && merchantId) {
      dispatch(fetchCustomerLoyalties({ merchantId, page: currentPage, limit }));
    } else if (!isAdmin) {
      dispatch(fetchCustomerLoyalties({ merchantId: '', page: currentPage, limit }));
    }
  }, [dispatch, merchantId, currentPage, limit, isAdmin]);

  const theme: DefaultTheme = {
    mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark',
  };

  const handlePaginationChange = (page: number, size: number) => {
    if (size !== limit) {
      dispatch(setLimit(size));
    }
    if (page !== currentPage) {
      dispatch(setCurrentPage(page));
    }
  };

  const handleMerchantSelect = (merchant: { id: string | number }) => {
    dispatch(setMerchantId(String(merchant.id)));
  };

  return (
    <ThemeProvider theme={theme}>
      <Container>
      <HeaderContainer>
        <HeaderContent>
          <IconWrapper
            $bgColor={isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)'}
            $color="#3b82f6"
          >
            <FontAwesomeIcon icon={faCrown} style={{ fontSize: 24 }} />
          </IconWrapper>
          <div>
            <HeaderTitle level={2}>
              Loyalty Program
            </HeaderTitle>
            <HeaderSubtitle>
              Manage customer loyalty and rewards system
            </HeaderSubtitle>
          </div>
        </HeaderContent>

        {isAdmin && (
          <CustomersDropdown onOptionSelect={handleMerchantSelect} />
        )}
      </HeaderContainer>

      <SummaryStats customerLoyalties={customerLoyalties} config={config} />

      <Configuration />

      <CustomersCard>
        <CustomersHeader>
          <CustomersHeaderContent>
            <IconWrapper
              $bgColor={isDarkMode ? 'rgba(20, 184, 166, 0.1)' : 'rgba(20, 184, 166, 0.05)'}
              $color="#14b8a6"
            >
              <FontAwesomeIcon icon={faCrown} style={{ fontSize: 18 }} />
            </IconWrapper>
            <HeaderText $isDarkMode={isDarkMode}>
              Customer Loyalty
            </HeaderText>
            <CustomerCountBadge>
              {customerLoyalties.length} customers
            </CustomerCountBadge>
          </CustomersHeaderContent>
        </CustomersHeader>

        <CustomerTable
          customerLoyalties={customerLoyalties}
          totalPages={totalPages}
          currentPage={currentPage}
          limit={limit}
          totalCount={totalCount}
          onPaginationChange={handlePaginationChange}
          loading={loading.customers}
        />
      </CustomersCard>
      </Container>
    </ThemeProvider>
  );
};

export default Loyalty;
